import React, { useContext, useEffect, useState } from "react";
import { Card, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Theme, Box, SxProps } from "@mui/material";
import { formatRelativeTime } from "Backend";
import { LightbulbDisabledIcon, LightbulbIcon } from "../Icons";
import { P } from "theme/typography";
import { BooleanCell } from "./BooleanCell";
import { AutoAwesome, BarChart, GridOn } from "@mui/icons-material";
import Entries from "./Entries";
import { CaseFormContext } from "contexts/CaseFormContext";
import { FormFieldContext } from "contexts/FormFieldContext";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "components/LoadingSpinner";
import useIsInViewport from "helpers/hooks/useIsInViewport";
import theme from "theme";
import { ProfileGenRunInfo } from "../../helpers/hooks/useCaseForm";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { CaseDisplayGroups } from "routes/lenses/lens/case/CaseDisplayGroups";
import { useAppSelector } from "redux/hooks";
import { selectCaseDisplayGroups } from "redux/slices/clientLens";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";
import { FormFieldName } from "./FormFieldName";

interface FormFieldEditorProps {
  fieldRef?: React.RefObject<HTMLDivElement>;
  hideLightbulb?: boolean;
  activeSectionId?: number | undefined; // TODO: restore functionality that this was for
  sx?: SxProps<Theme>;
}

export const FormFieldEditor = (props: FormFieldEditorProps) => {
  const navigate = useNavigate();
  const { hideLightbulb, sx } = props;
  const ffContext = useContext(FormFieldContext);
  const cfContext = useContext(CaseFormContext);
  const linkToReview = cfContext.linkToReview(ffContext.formField.id);
  const findingsNotLoaded = ffContext.hasFindings === undefined;
  const [fieldView, setFieldView] = useState<"grouped" | "timeline">("grouped");
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const displayGroupLocations = useGroupLocations(caseDisplayGroups);

  // Cannot use props.fieldRef because sometimes its undefined due to a bug
  const [lightbulbRef, setLightbulbRef] = useState<HTMLButtonElement | null>(null);
  const isVisible = useIsInViewport(lightbulbRef || undefined);

  useEffect(() => {
    if (isVisible) {
      ffContext.loadHasFindings();
    }
  }, [isVisible]);

  const goToReview = (route: string) => {
    navigate(route);
  };

  const showForm =
    ffContext.state.yesNoValue !== false &&
    (ffContext.formField.promptYesNo ? ffContext.state.yesNoValue === true : true);

  const hasNoDateData = !ffContext.hasAnyDateCellsWithValue;

  const makeProfileGenRunSummaryTooltip = (lastRun: NonNullable<ProfileGenRunInfo["lastRun"]>) =>
    `Auto Review. Last run on ${formatRelativeTime(lastRun.timestamp)}`;

  return (
    <Box id={`form-field-${ffContext.formField.id}`}>
      <Card
        variant="outlined"
        sx={{
          marginBottom: 1,
          padding: 2,
          width: "100%",
          ...sx,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}>
            {ffContext.profileGenRunSummary?.lastRun !== undefined && (
              <Tooltip title={makeProfileGenRunSummaryTooltip(ffContext.profileGenRunSummary.lastRun)}>
                <AutoAwesome sx={{ color: "primary.main", mr: 1 }} />
              </Tooltip>
            )}
            <FormFieldName formField={ffContext.formField} />
            <DescriptionTooltip description={ffContext.formField.description} sx={{ ml: 1 }} />
            {hideLightbulb !== true && (
              <Tooltip placement="right" arrow title={linkToReview ? "Review suggestions" : "No suggestions available"}>
                <span>
                  <IconButton
                    ref={(node) => setLightbulbRef(node)}
                    id={"pfield_lightbulb_" + ffContext.formField.id}
                    disabled={linkToReview === undefined || findingsNotLoaded}
                    onClick={() => linkToReview && goToReview(linkToReview)}>
                    {findingsNotLoaded ? (
                      <LoadingSpinner size={16} />
                    ) : linkToReview === undefined ? (
                      <LightbulbDisabledIcon
                        style={{
                          opacity: 0.5,
                        }}
                        size={20}
                      />
                    ) : (
                      <LightbulbIcon size={20} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {ffContext.hasAnyDateCells && (
            <ToggleButtonGroup
              sx={{ mr: 1 }}
              size="small"
              value={fieldView}
              exclusive
              onChange={(_evt, view) => setFieldView(view)}>
              <ToggleButton
                value="grouped"
                data-pendo={`grouped-view-${ffContext.formField.id}`}
                name={`grouped-view-${ffContext.formField.id}`}>
                <GridOn />
              </ToggleButton>
              <Tooltip title={hasNoDateData && "No date data in responses"}>
                <span>
                  <ToggleButton
                    value="timeline"
                    data-pendo={`timeline-view-${ffContext.formField.id}`}
                    disabled={hasNoDateData}
                    name={`timeline-view-${ffContext.formField.id}`}>
                    <BarChart />
                  </ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
          )}
        </Box>

        <div className="flex flex-col my-2">
          <P
            variant="xs"
            sx={{
              ...theme.mixins.centeredTextLike,
              marginRight: theme.spacing(1),
            }}>
            {ffContext.formField.prompt}
          </P>
          {ffContext.formField.promptYesNo && (
            <BooleanCell value={ffContext.state.yesNoValue} setValue={ffContext.saveYesNo} />
          )}
        </div>
        <CaseDisplayGroups
          groups={(displayGroupLocations.fields[ffContext.formField.id] ?? []).map((location) => location.group)}
        />
        {showForm && <Entries viewMode={fieldView} />}
      </Card>
    </Box>
  );
};

export const MemoFormFieldEditor = React.memo(FormFieldEditor);
